import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import { PUBLIC_SWR_KEYS } from "../Utilities/swrKeys";
import { useCachedSWR } from "../Utilities/useCachedSWR";
/**
 * Hook for accessing cached plans data.
 *
 * Plans are program configurations (e.g., usesLocation, usesZipCodes, usesChooseMeals)
 * that are hydrated on app load and cached in SWR.
 *
 * This is a cache-only hook (no fetcher) - data must be pre-populated by PublicDataHydrator.
 * Components use this to instantly look up plan properties by programId without API calls.
 *
 * @example
 * ```typescript
 * const { data, isLoading } = usePlansQuery();
 * const plan = data?.data?.find(p => p.programId === programId);
 * const usesLocation = plan?.usesLocation;
 * ```
 */
export const usePlansQuery = () => {
    const { datadogLog } = useLogging();
    const { data, error, isLoading } = useCachedSWR(PUBLIC_SWR_KEYS.PLANS, {
        onSuccess: (data) => {
            if (!data.success) {
                datadogLog("Failed to get Plans data.", LogLevel.ERROR, undefined);
                throw new Error("Something unexpected happened while loading Plans data.");
            }
        },
        onError: (error) => {
            datadogLog("Failed to get Plans data.", LogLevel.ERROR, undefined, error);
        },
    });
    return { data, error, isLoading };
};
